import React, { useEffect, useState } from "react";
import { ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

/**
 * LevelTwoGuard gates sensitive analytical screens (Dashboard, MR) behind the
 * Admin / Level-2 approver password. Unlock is verified server-side via
 * `POST /api/settings/verify-approval-password` so the gate cannot be bypassed
 * by reading the bundled JS. Unlock state is cached in sessionStorage for the
 * tab lifetime so users don't re-type for every navigation.
 */
const STORAGE_KEY = "cpms_l2_unlocked";

export default function LevelTwoGuard({ children, label = "Restricted Section", title = "Admin Access Required", note = "Enter the Admin / Level-2 approver password to view this section." }) {
  const [unlocked, setUnlocked] = useState(
    () => typeof window !== "undefined" && sessionStorage.getItem(STORAGE_KEY) === "1"
  );
  const [pwd, setPwd] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setError("");
    setPwd("");
  }, [unlocked]);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!pwd.trim()) {
      setError("Password required");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/settings/verify-approval-password", { password: pwd, level: 2 });
      sessionStorage.setItem(STORAGE_KEY, "1");
      setUnlocked(true);
      toast.success("Unlocked for this session");
    } catch (err) {
      const detail = err?.response?.data?.detail || "Verification failed";
      setError(detail);
      setPwd("");
    } finally {
      setSubmitting(false);
    }
  };

  if (unlocked) return children;

  return (
    <div className="min-h-[60vh] flex items-center justify-center" data-testid="l2-guard">
      <div className="bg-white border border-slate-200 rounded-lg shadow-sm w-full max-w-sm p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-md bg-amber-100 flex items-center justify-center">
            <ShieldCheck size={18} className="text-amber-700" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
            <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
          </div>
        </div>
        <p className="text-sm text-slate-600 mb-4">
          {note} <span className="text-slate-400">Stays unlocked until you close this tab.</span>
        </p>
        <form onSubmit={submit} className="space-y-3">
          <input
            autoFocus
            type="password"
            value={pwd}
            onChange={(e) => setPwd(e.target.value)}
            placeholder="Admin / Level-2 password"
            data-testid="l2-guard-password-input"
            className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1D2951]/20 focus:border-[#1D2951]"
          />
          {error && <div className="text-xs text-rose-600 font-semibold" data-testid="l2-guard-error">{error}</div>}
          <button
            type="submit"
            disabled={submitting}
            data-testid="l2-guard-submit"
            className="w-full bg-[#1D2951] hover:bg-[#2B3A67] disabled:opacity-60 text-white font-semibold text-sm px-4 py-2 rounded-md"
          >
            {submitting ? "Verifying…" : "Unlock"}
          </button>
        </form>
      </div>
    </div>
  );
}
